import React, { useEffect, useMemo, useState } from 'react';
import { Input, Card, Row, Col, Spin, Empty, Space, Tag, Typography, Avatar, Tooltip } from 'antd';
import {
    SearchOutlined,
    CheckCircleOutlined,
    ThunderboltOutlined,
    EditOutlined,
    DollarOutlined,
    EyeOutlined,
    ApiOutlined,
} from '@ant-design/icons';
import {
    formatChatCatalogContext,
    formatChatCatalogCostPerMillion,
} from '../collections/collectionUtils';
import { resolveModelIconUrl } from '../../utils/modelIconUrl';
import './ModelFields.css';

const { Text, Paragraph } = Typography;

const DEBOUNCE_MS = 200;

/**
 * Cloud catalog model picker: search + cards (same metadata as Add Assistant: context, pricing, capabilities)
 * + manual LiteLLM id field.
 */
const CatalogChatModelField = ({
    models,
    loading,
    search,
    onSearchChange,
    value,
    onChange,
    emptyDescription,
}) => {
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search || ''), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);

    const filtered = useMemo(() => {
        const q = debouncedSearch.trim().toLowerCase();
        if (!q) return models;
        return models.filter(
            (m) =>
                (m.display_name || '').toLowerCase().includes(q) ||
                (m.litellm_model_name || '').toLowerCase().includes(q),
        );
    }, [models, debouncedSearch]);

    const searchTrim = debouncedSearch.trim();
    const exactInCatalog = searchTrim
        ? models.some((m) => (m.litellm_model_name || m.fullName) === searchTrim)
        : false;
    const showUseSearchAsModel = Boolean(
        searchTrim.length > 0 &&
            !loading &&
            !exactInCatalog &&
            filtered.length === 0,
    );
    const manualFromSearchSelected = Boolean(
        searchTrim &&
            value != null &&
            value.trim() === searchTrim &&
            !models.some((m) => (m.litellm_model_name || m.fullName) === searchTrim),
    );

    return (
        <div className="catalog-chat-model-field">
            <Input
                size="large"
                placeholder="Search catalog…"
                prefix={<SearchOutlined style={{ color: 'var(--ant-color-text-quaternary, #bbb)' }} />}
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                allowClear
                className="catalog-chat-model-field-search"
            />
            <div className="catalog-chat-model-field-list create-collection-model-scroll">
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 32 }}>
                        <Spin />
                    </div>
                ) : filtered.length > 0 ? (
                    <Row gutter={[8, 8]}>
                        {filtered.map((m) => {
                            const pick = m.litellm_model_name || m.fullName;
                            const isSelected = value != null && value.trim() === pick;
                            const ctxLabel = formatChatCatalogContext(m.context_window);
                            const inCost = formatChatCatalogCostPerMillion(m.input_cost_per_token);
                            const outCost = formatChatCatalogCostPerMillion(m.output_cost_per_token);
                            const cardIconSrc = m.icon ? resolveModelIconUrl(m.icon) : null;
                            return (
                                <Col span={24} key={m.id ?? m.litellm_model_name}>
                                    <Card
                                        size="small"
                                        hoverable
                                        onClick={() => onChange(pick)}
                                        className={`catalog-chat-model-card create-collection-model-card ${isSelected ? 'is-selected' : ''}`}
                                        bodyStyle={{ padding: '12px 14px' }}
                                    >
                                        <div className="catalog-chat-model-card-inner">
                                            <div className="catalog-chat-model-card-row1">
                                                {cardIconSrc ? (
                                                    <Avatar
                                                        src={cardIconSrc}
                                                        size={36}
                                                        shape="square"
                                                        style={{ flexShrink: 0 }}
                                                    />
                                                ) : m.familyIcon ? (
                                                    <img
                                                        src={resolveModelIconUrl(m.familyIcon)}
                                                        alt=""
                                                        className="create-collection-embedding-card-icon"
                                                    />
                                                ) : (
                                                    <div className="create-collection-embedding-card-icon-fallback">
                                                        <ThunderboltOutlined />
                                                    </div>
                                                )}
                                                <div className="catalog-chat-model-card-titles">
                                                    <Text strong style={{ fontSize: 14, display: 'block' }}>
                                                        {m.display_name || m.litellm_model_name}
                                                    </Text>
                                                    {m.display_name &&
                                                        m.display_name !== m.litellm_model_name &&
                                                        m.litellm_model_name && (
                                                            <Text
                                                                type="secondary"
                                                                style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                                                                ellipsis
                                                            >
                                                                {m.litellm_model_name}
                                                            </Text>
                                                        )}
                                                </div>
                                            </div>
                                            {m.familyDescription && (
                                                <Paragraph
                                                    type="secondary"
                                                    ellipsis={{ rows: 2 }}
                                                    className="catalog-chat-model-card-family-desc"
                                                >
                                                    {m.familyDescription}
                                                </Paragraph>
                                            )}
                                            <div className="catalog-chat-model-card-tags">
                                                {ctxLabel && (
                                                    <Tag color="blue">
                                                        {ctxLabel} ctx
                                                    </Tag>
                                                )}
                                                {m.supports_vision && (
                                                    <Tooltip title="Supports vision">
                                                        <Tag color="purple">
                                                            <EyeOutlined />
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                                {m.supports_function_calling && (
                                                    <Tooltip title="Function calling">
                                                        <Tag color="cyan">
                                                            <ApiOutlined />
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                                {m.size && <Tag>{m.size}</Tag>}
                                                {m.context && typeof m.context === 'string' && (
                                                    <Tag>{m.context}</Tag>
                                                )}
                                                {inCost && (
                                                    <Tooltip title="Input pricing (approx. per 1M tokens)">
                                                        <Tag color="green">
                                                            <DollarOutlined /> In {inCost}
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                                {outCost && (
                                                    <Tooltip title="Output pricing (approx. per 1M tokens)">
                                                        <Tag color="geekblue">
                                                            <DollarOutlined /> Out {outCost}
                                                        </Tag>
                                                    </Tooltip>
                                                )}
                                            </div>
                                            {isSelected && (
                                                <CheckCircleOutlined
                                                    className="create-collection-choice-card-check"
                                                    aria-hidden
                                                />
                                            )}
                                        </div>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                ) : showUseSearchAsModel ? (
                    <Row gutter={[8, 8]}>
                        <Col span={24}>
                            <Card
                                size="small"
                                hoverable
                                role="button"
                                tabIndex={0}
                                onClick={() => onChange(searchTrim)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onChange(searchTrim);
                                    }
                                }}
                                className={`create-collection-model-card local-ollama-use-custom-card ${manualFromSearchSelected ? 'is-selected' : ''}`}
                                bodyStyle={{ padding: '10px 12px' }}
                            >
                                <div className="create-collection-embedding-card-inner">
                                    <div className="create-collection-embedding-card-main">
                                        <div className="create-collection-embedding-card-icon-fallback local-ollama-use-custom-icon">
                                            <EditOutlined />
                                        </div>
                                        <div className="create-collection-embedding-card-text">
                                            <Text strong className="create-collection-embedding-card-name">
                                                Use this model id
                                            </Text>
                                            <Paragraph type="secondary" className="local-ollama-use-custom-code" copyable={{ text: searchTrim }}>
                                                {searchTrim}
                                            </Paragraph>
                                            <Text type="secondary" className="local-ollama-use-custom-hint">
                                                No catalog match — uses this LiteLLM id as entered (also editable below).
                                            </Text>
                                        </div>
                                    </div>
                                    {manualFromSearchSelected && (
                                        <CheckCircleOutlined
                                            className="create-collection-choice-card-check"
                                            aria-hidden
                                        />
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyDescription} />
                )}
            </div>
            <div className="catalog-chat-model-manual">
                <Text type="secondary" className="catalog-chat-model-manual-label">
                    Model identifier
                </Text>
                <Input
                    size="large"
                    placeholder="Matches your selection above, or enter a custom LiteLLM model id"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    className="catalog-chat-model-manual-input"
                />
            </div>
        </div>
    );
};

export default CatalogChatModelField;
